import { useCallback, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { serversApi } from '../../api'
import { useTagStreamHub } from '../../hooks/useSignalR'
import type { EdgeMachineItem, EdgeMachineItemEdit, EdgeTag, EdgeTagValue, MachineItemKind, MachineLogType } from '../../types'

export const LOG_TYPES: MachineLogType[] = ['Alarm', 'Parameter', 'Mete', 'Signal']

export const TYPE_LABELS: Record<MachineLogType, string> = {
  Alarm: 'ALARM',
  Parameter: 'PARAMETER',
  Mete: 'METE',
  Signal: 'SIGNAL',
}

export const TYPE_COLORS: Record<MachineLogType, string> = {
  Alarm: 'bg-red-100 text-red-700',
  Parameter: 'bg-blue-100 text-blue-700',
  Mete: 'bg-green-100 text-green-700',
  Signal: 'bg-purple-100 text-purple-700',
}

export function formatTime(iso: string) {
  const d = new Date(iso)
  return d.toLocaleString('ko-KR', { hour12: false })
}

// 태그 값 1건 — rev 는 스트림 갱신 횟수 (값 셀 key 로 써서 갱신마다 하이라이트 재생, 0 = 스냅샷 값)
export interface TagReading {
  value: string
  time: string
  rev: number
}

// 태그 값 스트림의 키 — 엣지 결속 키 (TagMaster.RuntimeCode)
export const tagStreamKey = (t: EdgeTag) => t.externalCode ?? t.code

// 스트림 값이 스냅샷(GetDevices 의 lastValue)보다 새로우면 스트림 값, 아니면 스냅샷 값
export function currentReading(tag: EdgeTag, live: Record<string, TagReading>): TagReading | null {
  const streamed = live[tagStreamKey(tag)]
  const snapshot = tag.lastValue != null && tag.lastTime
    ? { value: tag.lastValue, time: tag.lastTime, rev: 0 }
    : null
  if (!streamed) return snapshot
  if (!snapshot) return streamed
  return Date.parse(streamed.time) >= Date.parse(snapshot.time) ? streamed : snapshot
}

// 디바이스·태그 설정 스냅샷 — 조회마다 엣지에 왕복하므로 자동 재조회는 끄고 새로고침으로만 다시 받는다.
// 디바이스 탭과 마스터데이터 설정이 같은 캐시를 쓴다.
export function useEdgeDevices(serverId: string, enabled = true) {
  return useQuery({
    queryKey: ['servers', serverId, 'devices'],
    queryFn: () => serversApi.getDevices(serverId).then(r => r.data),
    enabled,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

// 라인 › 설비 마스터데이터 — 디바이스와 같은 이유로 자동 재조회하지 않는다
export function useEdgeMachines(serverId: string) {
  return useQuery({
    queryKey: ['servers', serverId, 'machines'],
    queryFn: () => serversApi.getMachines(serverId).then(r => r.data),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

// 마운트 동안 태그 값 스트림을 받아 결속 키별 최신값을 모은다 (Edge팀 전용 — 아니면 serverId 를 넘기지 않는다)
export function useLiveTagValues(serverId: string | undefined) {
  const [live, setLive] = useState<Record<string, TagReading>>({})

  const onTagValues = useCallback((values: EdgeTagValue[]) => {
    setLive(prev => {
      const next = { ...prev }
      for (const v of values) {
        const cur = next[v.tagCode]
        // 수집 시각이 더 최신일 때만 교체 (배치 순서가 뒤섞여도)
        if (cur && Date.parse(cur.time) > Date.parse(v.time)) continue
        next[v.tagCode] = { value: v.value, time: v.time, rev: (cur?.rev ?? 0) + 1 }
      }
      return next
    })
  }, [])

  useTagStreamHub(serverId, onTagValues)
  return live
}

// 태그 조회 결과 — 디바이스 목록을 아직 못 받았으면 undefined(판단 보류), 받았는데 없으면 null(끊어진 연결)
export type TagResolved = EdgeTag | null | undefined

export type ItemListKey = 'alarms' | 'metes' | 'params' | 'signals'

// Alarm·Mete·Parameter·Signal — 섹션 순서·이름은 LiveEdge 마스터데이터와 같게 영어로
export const ITEM_SECTIONS: { key: ItemListKey; title: string; kind: MachineItemKind }[] = [
  { key: 'alarms', title: 'Alarm', kind: 'alarm' },
  { key: 'metes', title: 'Mete', kind: 'mete' },
  { key: 'params', title: 'Parameter', kind: 'param' },
  { key: 'signals', title: 'Signal', kind: 'signal' },
]

/** 엣지에 있는 항목을 그대로 다시 보낼 형식으로 (목록 통째 교체라 기존 항목도 같이 보낸다) */
export function itemToEdit(item: EdgeMachineItem, kind: MachineItemKind): EdgeMachineItemEdit {
  const changedOnly = item.changedOnly ?? true
  return {
    tagId: item.tagId,
    code: item.code.trim(),
    description: item.description?.trim() || null,
    mesRegisterable: item.mesRegisterable,
    alarmValue: kind === 'alarm' ? (item.alarmValue ?? '').trim() : null,
    changedOnly,
    mesInterval: kind === 'signal' && item.mesRegisterable && !changedOnly ? item.mesInterval ?? 5000 : null,
  }
}

// MES 전송 — 등록 안 하면 '-'. 신호는 변경 시 전송이거나 주기 전송 둘 중 하나 (MesWorker)
export function mesLabel(item: Pick<EdgeMachineItem, 'mesRegisterable' | 'changedOnly' | 'mesInterval'>, kind: MachineItemKind) {
  if (!item.mesRegisterable) return '-'
  if (kind !== 'signal') return '등록'
  return item.changedOnly ? '변경 시' : `${item.mesInterval ?? '-'}ms 주기`
}
